// =============================================================================
// SHARE CARD GENERATOR - Canvas-based image generation for social sharing
// =============================================================================

import { CARD_DIMENSIONS, STORIES_DIMENSIONS } from './canvas/config';
import { loadLogo } from './canvas/utils';

export interface ShareCardData {
  type: 'victory' | 'stalemate' | 'defeat' | 'gameOver';
  score: number;
  stats: {
    militaryStrength: number;
    economy: number;
    resilience: number;
    westernSupport: number;
    trust: number;
  };
  decisionNumber?: number;
  gameOverType?: string;
}

interface CardTheme {
  background: [string, string]; // gradient colors
  accentColor: string;
  titleText: string;
  subtitleText: string;
}

const CARD_THEMES: Record<ShareCardData['type'], CardTheme> = {
  victory: {
    background: ['#0a1a0a', '#0d2818'],
    accentColor: '#10b981',
    titleText: 'СТРАТЕГІЧНА ПЕРЕВАГА',
    subtitleText: 'Операція завершена успішно',
  },
  stalemate: {
    background: ['#1a1a0a', '#2d2810'],
    accentColor: '#f59e0b',
    titleText: 'НІЯКА СИТУАЦІЯ',
    subtitleText: 'Боротьба продовжується',
  },
  defeat: {
    background: ['#1a0a0a', '#2d1010'],
    accentColor: '#ef4444',
    titleText: 'ОПЕРАЦІЙНИЙ ПРОВАЛ',
    subtitleText: 'Критичні втрати зафіксовано',
  },
  gameOver: {
    background: ['#1a0a0a', '#2d0808'],
    accentColor: '#dc2626',
    titleText: 'ЗАВАЛ',
    subtitleText: 'Система командування втрачена',
  },
};

const STAT_LABELS: Record<
  keyof ShareCardData['stats'],
  { label: string; emoji: string }
> = {
  militaryStrength: { label: 'СИЛА ЗС', emoji: '🪖' },
  economy: { label: 'ЕКОНОМІКА', emoji: '💰' },
  resilience: { label: 'СТІЙКІСТЬ', emoji: '💪' },
  westernSupport: { label: 'ПІДТРИМКА', emoji: '🤝' },
  trust: { label: 'ДОВІРА', emoji: '🏛️' },
};

// Card dimensions (optimized for mobile sharing - 4:5 ratio for Instagram/Facebook)
const { width: CARD_WIDTH, height: CARD_HEIGHT } = CARD_DIMENSIONS;

/**
 * Generate share card image as data URL
 */
export async function generateShareCard(data: ShareCardData): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = CARD_WIDTH;
  canvas.height = CARD_HEIGHT;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  const theme = CARD_THEMES[data.type];

  // Draw background gradient
  const gradient = ctx.createLinearGradient(0, 0, CARD_WIDTH, CARD_HEIGHT);
  gradient.addColorStop(0, theme.background[0]);
  gradient.addColorStop(1, theme.background[1]);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, CARD_WIDTH, CARD_HEIGHT);

  // Draw grid pattern
  drawGridPattern(ctx, theme.accentColor);

  // Draw corner accents
  drawCornerAccents(ctx, theme.accentColor);

  // Draw top header bar
  drawHeaderBar(ctx, theme.accentColor);

  // Draw Logo
  const logo = await loadLogo();
  if (logo) {
    const logoSize = 120;
    ctx.globalAlpha = 0.8;
    ctx.drawImage(logo, (CARD_WIDTH - logoSize) / 2, 130, logoSize, logoSize);
    ctx.globalAlpha = 1;
  }

  // Draw main title
  drawTitle(ctx, theme);

  // Draw score
  drawScore(ctx, data.score, theme.accentColor);

  // Draw stats
  drawStats(ctx, data.stats, theme.accentColor);

  // Draw footer
  drawFooter(ctx, theme.accentColor, data.decisionNumber);

  // Draw branding
  drawBranding(ctx, theme.accentColor);

  return canvas.toDataURL('image/png', 1);
}

function drawGridPattern(ctx: CanvasRenderingContext2D, color: string): void {
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.03;
  ctx.lineWidth = 1;

  // Vertical lines
  for (let x = 0; x < CARD_WIDTH; x += 40) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, CARD_HEIGHT);
    ctx.stroke();
  }

  // Horizontal lines
  for (let y = 0; y < CARD_HEIGHT; y += 40) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(CARD_WIDTH, y);
    ctx.stroke();
  }

  ctx.globalAlpha = 1;
}

function drawCornerAccents(ctx: CanvasRenderingContext2D, color: string): void {
  const size = 60;
  const thickness = 4;
  const margin = 30;

  ctx.strokeStyle = color;
  ctx.lineWidth = thickness;
  ctx.globalAlpha = 0.6;

  // Top-left
  ctx.beginPath();
  ctx.moveTo(margin, margin + size);
  ctx.lineTo(margin, margin);
  ctx.lineTo(margin + size, margin);
  ctx.stroke();

  // Top-right
  ctx.beginPath();
  ctx.moveTo(CARD_WIDTH - margin - size, margin);
  ctx.lineTo(CARD_WIDTH - margin, margin);
  ctx.lineTo(CARD_WIDTH - margin, margin + size);
  ctx.stroke();

  // Bottom-left
  ctx.beginPath();
  ctx.moveTo(margin, CARD_HEIGHT - margin - size);
  ctx.lineTo(margin, CARD_HEIGHT - margin);
  ctx.lineTo(margin + size, CARD_HEIGHT - margin);
  ctx.stroke();

  // Bottom-right
  ctx.beginPath();
  ctx.moveTo(CARD_WIDTH - margin - size, CARD_HEIGHT - margin);
  ctx.lineTo(CARD_WIDTH - margin, CARD_HEIGHT - margin);
  ctx.lineTo(CARD_WIDTH - margin, CARD_HEIGHT - margin - size);
  ctx.stroke();

  ctx.globalAlpha = 1;
}

function drawHeaderBar(ctx: CanvasRenderingContext2D, color: string): void {
  // Header background
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.1;
  ctx.fillRect(60, 60, CARD_WIDTH - 120, 50);
  ctx.globalAlpha = 1;

  // Header text
  ctx.font = '600 14px "SF Mono", "Fira Code", monospace';
  ctx.fillStyle = color;
  ctx.textAlign = 'left';
  ctx.fillText('ГОЛОВНОКОМАНДУВАЧ // ПІДСУМКОВИЙ ЗВІТ', 80, 92);

  // Right side classification
  ctx.textAlign = 'right';
  ctx.globalAlpha = 0.5;
  ctx.fillText('TOP SECRET // EYES ONLY', CARD_WIDTH - 80, 92);
  ctx.globalAlpha = 1;
}

function drawTitle(ctx: CanvasRenderingContext2D, theme: CardTheme): void {
  // Main title - moved down to accommodate logo
  ctx.font = 'bold 52px "SF Pro Display", "Inter", sans-serif';
  ctx.fillStyle = theme.accentColor;
  ctx.textAlign = 'center';
  ctx.fillText(theme.titleText, CARD_WIDTH / 2, 300);

  // Subtitle
  ctx.font = '300 22px "SF Pro Display", "Inter", sans-serif';
  ctx.fillStyle = '#a3a3a3';
  ctx.fillText(theme.subtitleText, CARD_WIDTH / 2, 345);
}

function drawScore(
  ctx: CanvasRenderingContext2D,
  score: number,
  color: string,
): void {
  const centerX = CARD_WIDTH / 2;
  const centerY = 530;
  const radius = 120;

  // Score circle background
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.1;
  ctx.fill();
  ctx.globalAlpha = 1;

  // Score circle border
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
  ctx.strokeStyle = color;
  ctx.lineWidth = 5;
  ctx.stroke();

  // Score progress arc
  const startAngle = -Math.PI / 2;
  const endAngle = startAngle + (Math.PI * 2 * score) / 100;
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, startAngle, endAngle);
  ctx.strokeStyle = color;
  ctx.lineWidth = 12;
  ctx.stroke();

  // Score text
  ctx.font = 'bold 90px "SF Pro Display", "Inter", sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`${Math.round(score)}`, centerX, centerY - 10);

  // Percent sign
  ctx.font = 'bold 32px "SF Pro Display", "Inter", sans-serif';
  ctx.fillStyle = color;
  ctx.fillText('%', centerX, centerY + 50);

  ctx.textBaseline = 'alphabetic';
}

function drawStats(
  ctx: CanvasRenderingContext2D,
  stats: ShareCardData['stats'],
  color: string,
): void {
  const statKeys = Object.keys(stats) as (keyof typeof stats)[];
  const startY = 750;
  const rowHeight = 110;

  // Draw stats in a vertical list for mobile
  statKeys.forEach((key, index) => {
    const y = startY + index * rowHeight;
    const value = Math.round(stats[key]);
    const { label, emoji } = STAT_LABELS[key];

    // Stat background
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.05;
    ctx.fillRect(60, y - 35, CARD_WIDTH - 120, 90);
    ctx.globalAlpha = 1;

    // Emoji
    ctx.font = '36px "Apple Color Emoji", "Segoe UI Emoji", sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(emoji, 90, y + 12);

    // Label
    ctx.font = '600 16px "SF Mono", "Fira Code", monospace';
    ctx.fillStyle = '#a3a3a3';
    ctx.fillText(label, 150, y + 8);

    // Value with progress bar
    const barWidth = 300;
    const barX = CARD_WIDTH - 90 - barWidth;
    const barY = y - 8;
    const barHeight = 32;

    // Background bar
    ctx.fillStyle = '#1a1a1a';
    ctx.fillRect(barX, barY, barWidth, barHeight);

    // Value bar
    ctx.fillStyle = getStatColor(value);
    ctx.fillRect(barX, barY, (barWidth * value) / 100, barHeight);

    // Value text
    ctx.font = 'bold 22px "SF Pro Display", "Inter", sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'right';
    ctx.fillText(`${value}%`, CARD_WIDTH - 90, y + 12);
  });
}

function getStatColor(value: number): string {
  if (value >= 70) return '#10b981';
  if (value >= 40) return '#f59e0b';
  return '#ef4444';
}

function drawFooter(
  ctx: CanvasRenderingContext2D,
  color: string,
  decisionNumber?: number,
): void {
  const y = CARD_HEIGHT - 50;

  // Footer line
  ctx.strokeStyle = color;
  ctx.globalAlpha = 0.2;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(60, y - 20);
  ctx.lineTo(CARD_WIDTH - 60, y - 20);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Footer text
  ctx.font = '500 12px "SF Mono", "Fira Code", monospace';
  ctx.fillStyle = '#525252';
  ctx.textAlign = 'left';
  ctx.fillText(
    decisionNumber ? `РІШЕННЯ: ${decisionNumber}/20` : 'РІШЕНЬ: 20/20',
    80,
    y,
  );

  ctx.textAlign = 'right';
  ctx.fillText('holovkom.zavoruha.tech', CARD_WIDTH - 80, y);
}

function drawBranding(ctx: CanvasRenderingContext2D, color: string): void {
  // CTA text
  ctx.font = '600 24px "SF Pro Display", "Inter", sans-serif';
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.fillText('ТИ ЗМОЖЕШ КРАЩЕ?', CARD_WIDTH / 2, CARD_HEIGHT - 100);
}

/**
 * Download the share card as PNG file
 */
export async function downloadShareCard(data: ShareCardData): Promise<void> {
  const dataUrl = await generateShareCard(data);

  const link = document.createElement('a');
  link.download = `holovkom-${data.type}-${Math.round(data.score)}.png`;
  link.href = dataUrl;
  link.click();
}

/**
 * Share card using Web Share API with image file
 */
export async function shareCardNative(
  data: ShareCardData,
  text: string,
): Promise<boolean> {
  if (!navigator.share || !navigator.canShare) {
    return false;
  }

  try {
    const dataUrl = await generateShareCard(data);
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const file = new File([blob], 'holovkom-result.png', { type: 'image/png' });

    const shareData = {
      files: [file],
      title: 'Головнокомандувач',
      text,
    };

    if (navigator.canShare(shareData)) {
      await navigator.share(shareData);
      return true;
    }

    // Fallback to text-only share if files not supported
    await navigator.share({ title: 'Головнокомандувач', text });
    return true;
  } catch (err) {
    if ((err as Error).name !== 'AbortError') {
      console.error('Share failed:', err);
    }
    return false;
  }
}

/**
 * Copy card image to clipboard (modern browsers)
 */
export async function copyCardToClipboard(
  data: ShareCardData,
): Promise<boolean> {
  if (!navigator.clipboard?.write) {
    return false;
  }

  try {
    const dataUrl = await generateShareCard(data);
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    await navigator.clipboard.write([
      new ClipboardItem({
        [blob.type]: blob,
      }),
    ]);

    return true;
  } catch (err) {
    console.error('Failed to copy image to clipboard:', err);
    return false;
  }
}

// =============================================================================
// CONSEQUENCES CARD - Viral social media design
// =============================================================================

export interface ConsequencesCardData {
  consequences: string;
  decisionNumber: number;
}

/**
 * Generate viral consequences card for social sharing - styled as app screenshot
 * Uses Instagram Stories dimensions (1080×1920)
 */
export async function generateConsequencesCard(
  data: ConsequencesCardData,
): Promise<string> {
  // Use Stories dimensions (9:16 vertical)
  const width = STORIES_DIMENSIONS.width;
  const height = STORIES_DIMENSIONS.height;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  // App background - dark tactical theme
  ctx.fillStyle = '#0a0f0a';
  ctx.fillRect(0, 0, width, height);

  // Subtle grid pattern like the app
  drawAppGridStories(ctx, width, height);

  // Draw phone status bar (fake)
  drawPhoneStatusBarStories(ctx, width);

  // Draw app HUD header
  await drawAppHeaderStories(ctx, width, data.decisionNumber);

  // Draw consequences content in app style
  drawAppConsequencesStories(ctx, width, height, data.consequences);

  // Draw bottom branding/CTA
  drawAppFooterStories(ctx, width, height);

  return canvas.toDataURL('image/png', 1);
}

function drawAppGridStories(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  ctx.strokeStyle = '#10b981';
  ctx.globalAlpha = 0.03;
  ctx.lineWidth = 1;

  // Vertical lines
  for (let x = 0; x < width; x += 30) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }

  // Horizontal lines
  for (let y = 0; y < height; y += 30) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }

  ctx.globalAlpha = 1;
}

function drawPhoneStatusBarStories(ctx: CanvasRenderingContext2D, width: number): void {
  const y = 50;

  // Time (left)
  ctx.font = '600 18px "SF Pro Display", sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'left';
  ctx.fillText('9:41', 50, y);

  // Icons (right) - simplified
  ctx.textAlign = 'right';
  ctx.font = '16px "SF Pro Display", sans-serif';
  ctx.fillText('📶  🔋', width - 50, y);
}

async function drawAppHeaderStories(ctx: CanvasRenderingContext2D, width: number, decisionNumber: number): Promise<void> {
  const headerY = 90;
  const headerHeight = 100;

  // Header background - like ParametersBar
  ctx.fillStyle = '#0d1810';
  ctx.globalAlpha = 0.8;
  ctx.fillRect(40, headerY, width - 80, headerHeight);
  ctx.globalAlpha = 1;

  // Border bottom
  ctx.strokeStyle = '#10b981';
  ctx.globalAlpha = 0.2;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(40, headerY + headerHeight);
  ctx.lineTo(width - 40, headerY + headerHeight);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Logo placeholder (left)
  const logo = await loadLogo();
  if (logo) {
    ctx.globalAlpha = 0.5;
    ctx.drawImage(logo, 60, headerY + 20, 60, 60);
    ctx.globalAlpha = 1;
  }

  // System title
  ctx.font = 'bold 22px "SF Mono", monospace';
  ctx.fillStyle = '#10b981';
  ctx.textAlign = 'left';
  ctx.fillText('СИСТЕМА УПРАВЛІННЯ', 140, headerY + 45);

  ctx.font = '14px "SF Mono", monospace';
  ctx.fillStyle = '#737373';
  ctx.fillText('СТАТУС: АКТИВНИЙ', 140, headerY + 70);

  // Decision counter (right)
  ctx.textAlign = 'right';
  ctx.font = '14px "SF Mono", monospace';
  ctx.fillStyle = '#737373';
  ctx.fillText('РІШЕННЯ', width - 60, headerY + 40);

  ctx.font = 'bold 28px "SF Mono", monospace';
  ctx.fillStyle = '#f5f5f5';
  const decStr = String(decisionNumber).padStart(2, '0');
  ctx.fillText(`${decStr} / 20`, width - 60, headerY + 75);
}

function drawAppConsequencesStories(ctx: CanvasRenderingContext2D, width: number, height: number, text: string): void {
  const contentY = 230;
  const padding = 50;
  const contentWidth = width - padding * 2;

  // Main consequence box - like the app's glass cards
  const boxY = contentY;
  const boxHeight = height - 550;

  // Glass effect background
  ctx.fillStyle = '#0d1810';
  ctx.globalAlpha = 0.6;
  ctx.fillRect(padding, boxY, contentWidth, boxHeight);
  ctx.globalAlpha = 1;

  // Border
  ctx.strokeStyle = '#10b981';
  ctx.globalAlpha = 0.3;
  ctx.lineWidth = 2;
  ctx.strokeRect(padding, boxY, contentWidth, boxHeight);
  ctx.globalAlpha = 1;

  // Corner accents
  const cornerSize = 30;
  ctx.strokeStyle = '#10b981';
  ctx.lineWidth = 4;

  // Top-left
  ctx.beginPath();
  ctx.moveTo(padding, boxY + cornerSize);
  ctx.lineTo(padding, boxY);
  ctx.lineTo(padding + cornerSize, boxY);
  ctx.stroke();

  // Top-right
  ctx.beginPath();
  ctx.moveTo(padding + contentWidth - cornerSize, boxY);
  ctx.lineTo(padding + contentWidth, boxY);
  ctx.lineTo(padding + contentWidth, boxY + cornerSize);
  ctx.stroke();

  // Bottom-left
  ctx.beginPath();
  ctx.moveTo(padding, boxY + boxHeight - cornerSize);
  ctx.lineTo(padding, boxY + boxHeight);
  ctx.lineTo(padding + cornerSize, boxY + boxHeight);
  ctx.stroke();

  // Bottom-right
  ctx.beginPath();
  ctx.moveTo(padding + contentWidth - cornerSize, boxY + boxHeight);
  ctx.lineTo(padding + contentWidth, boxY + boxHeight);
  ctx.lineTo(padding + contentWidth, boxY + boxHeight - cornerSize);
  ctx.stroke();

  // Section header with emphasis
  const sectionY = boxY + 60;
  ctx.font = 'bold 16px "SF Mono", monospace';
  ctx.fillStyle = '#ef4444';
  ctx.textAlign = 'center';
  ctx.fillText('⚠️ ВСЕ ЦЕ — НАСЛІДКИ ТВОГО РІШЕННЯ', width / 2, sectionY);

  // Divider line
  ctx.strokeStyle = '#ef4444';
  ctx.globalAlpha = 0.3;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(padding + 60, sectionY + 20);
  ctx.lineTo(padding + contentWidth - 60, sectionY + 20);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Main consequences text - wrapped and centered
  const textX = width / 2;
  const textStartY = sectionY + 100;
  const maxWidth = contentWidth - 80;
  const lineHeight = 50;

  ctx.font = '32px "SF Pro Display", Georgia, serif';
  ctx.fillStyle = '#f5f5f5';
  ctx.textAlign = 'center';

  // Word wrap
  const words = text.split(' ');
  let line = '';
  const lines: string[] = [];

  for (const word of words) {
    const testLine = line + word + ' ';
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && line !== '') {
      lines.push(line.trim());
      line = word + ' ';
    } else {
      line = testLine;
    }
  }
  lines.push(line.trim());

  // Draw text lines
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], textX, textStartY + i * lineHeight);
  }

  // Attribution
  const attrY = textStartY + lines.length * lineHeight + 50;
  ctx.font = '600 18px "SF Mono", monospace';
  ctx.fillStyle = '#10b981';
  ctx.fillText('— ГОЛОВНОКОМАНДУВАЧ', textX, attrY);

  // Warning indicator at bottom of box
  const warningY = boxY + boxHeight - 50;
  ctx.font = '14px "SF Mono", monospace';
  ctx.fillStyle = '#f59e0b';
  ctx.textAlign = 'center';
  ctx.globalAlpha = 0.9;
  ctx.fillText('ТИ ЗМІГ БИ ВЧИНИТИ КРАЩЕ?', textX, warningY);
  ctx.globalAlpha = 1;
}

function drawAppFooterStories(ctx: CanvasRenderingContext2D, width: number, height: number): void {
  const footerY = height - 280;

  // CTA block
  ctx.fillStyle = '#10b981';
  ctx.globalAlpha = 0.15;
  ctx.fillRect(50, footerY, width - 100, 90);
  ctx.globalAlpha = 1;

  // Border
  ctx.strokeStyle = '#10b981';
  ctx.lineWidth = 2;
  ctx.strokeRect(50, footerY, width - 100, 90);

  // CTA text
  ctx.font = 'bold 26px "SF Mono", monospace';
  ctx.fillStyle = '#10b981';
  ctx.textAlign = 'center';
  ctx.fillText('ПРИЙМИ КОМАНДУВАННЯ →', width / 2, footerY + 55);

  // URL and branding
  const brandingY = height - 150;
  ctx.font = '600 22px "SF Mono", monospace';
  ctx.fillStyle = '#10b981';
  ctx.globalAlpha = 0.9;
  ctx.fillText('holovkom.zavoruha.tech', width / 2, brandingY);
  ctx.globalAlpha = 1;

  // Hashtags
  ctx.font = '500 16px "SF Mono", monospace';
  ctx.fillStyle = '#525252';
  ctx.fillText('#Головнокомандувач #ЗАВОРУХА', width / 2, brandingY + 45);
}

/**
 * Download consequences card as PNG
 */
export async function downloadConsequencesCard(
  data: ConsequencesCardData,
): Promise<void> {
  const dataUrl = await generateConsequencesCard(data);
  const link = document.createElement('a');
  link.download = `holovkom-consequences-${data.decisionNumber}.png`;
  link.href = dataUrl;
  link.click();
}

/**
 * Share consequences card using native share
 */
export async function shareConsequencesNative(
  data: ConsequencesCardData,
): Promise<boolean> {
  if (!navigator.share || !navigator.canShare) {
    return false;
  }

  try {
    const dataUrl = await generateConsequencesCard(data);
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const file = new File([blob], 'holovkom-consequences.png', {
      type: 'image/png',
    });

    const shareData = {
      files: [file],
      title: 'Наслідки рішення — Головнокомандувач',
      text: `⚡ НАСЛІДКИ РІШЕННЯ #${data.decisionNumber}\n\n"${data.consequences}"\n\nПрийми командування: ${globalThis.location.origin}`,
    };

    if (navigator.canShare(shareData)) {
      await navigator.share(shareData);
      return true;
    }

    return false;
  } catch (err) {
    if ((err as Error).name !== 'AbortError') {
      console.error('Share failed:', err);
    }
    return false;
  }
}

/**
 * Copy consequences card to clipboard
 */
export async function copyConsequencesCard(
  data: ConsequencesCardData,
): Promise<boolean> {
  if (!navigator.clipboard?.write) {
    return false;
  }

  try {
    const dataUrl = await generateConsequencesCard(data);
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    await navigator.clipboard.write([
      new ClipboardItem({
        [blob.type]: blob,
      }),
    ]);

    return true;
  } catch (err) {
    console.error('Failed to copy consequences card:', err);
    return false;
  }
}
